import * as React from "react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function toLocalDatetimeValue(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Schedule the current draft. Server delivers it via the sweep loop
 * (60s granularity is fine for the demo cadence).
 */
export default function ScheduleSendDialog({ open, onOpenChange, companyId, conversation, body, priority, onScheduled }) {
  const [when, setWhen] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const d = new Date();
    d.setMinutes(d.getMinutes() + 15);
    d.setSeconds(0, 0);
    setWhen(toLocalDatetimeValue(d));
  }, [open]);

  async function submit() {
    if (!body || !body.trim()) { toast.error("Type a message first, then schedule."); return; }
    if (!when) return;
    const local = new Date(when);
    if (isNaN(local.getTime())) { toast.error("Pick a valid time."); return; }
    setBusy(true);
    try {
      await api.post(
        `/companies/${companyId}/comms/conversations/${conversation.id}/schedule`,
        { body: body.trim(), deliver_at: local.toISOString(), priority: priority || "normal" },
      );
      toast.success("Scheduled");
      onScheduled && onScheduled();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't schedule");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="schedule-send-dialog">
        <DialogHeader>
          <DialogTitle>Schedule send</DialogTitle>
          <DialogDescription>
            We'll deliver this message at the time you pick. Cancel any time
            from "Scheduled" before it goes out.
          </DialogDescription>
        </DialogHeader>
        <input
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
          className="field-input h-9 text-[13px] mono-num"
          data-testid="schedule-send-datetime"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="btn-ghost h-8" onClick={() => onOpenChange(false)}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="btn-violet h-8"
            data-testid="schedule-send-submit"
          >
            {busy ? "…" : "Schedule"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
